import mark from './mark';
import measure from './measure';
import logMeasurementToDD from './logMeasurementToDD';
import getBaselineMark from './getBaselineMark';
import supportsPerformanceAPI from './supportsPerformanceAPI';
import getStartMarkForMeasure from './getStartMarkForMeasure';

/**
 * Captures a measurement against the passed properties and logs it to DD (and locally, if applicable).
 * This also deals with differentiating an initial page load from a route change for the purpose of timings.
 *
 * @param opts Configuration options for the measurement
 * @param opts.name Name to attribute to the measurement
 * @param opts.addMark If true, a performance mark will be added with the same name as the measurement
 * @param opts.start If passed, the mark will be used at the startMark for the measurement
 * @param opts.end If passed, the mark will be used at the endMark for the measurement
 * @param opts.extra Any extra information that should be included in the logged measurement
 */
export default (opts: {
  name: string;
  addMark?: boolean | string;
  startMark?: string;
  endMark?: string;
  extra?: { [key: string]: unknown };
}): void => {
  if (!supportsPerformanceAPI()) {
    return;
  }

  try {
    const {
      name,
      addMark,
      startMark: startMarkName = undefined,
      endMark: endMarkName = undefined,
      extra = {},
    } = opts;
    if (addMark !== undefined) {
      mark(typeof addMark === 'string' ? addMark : name);
    }

    const baselineMark = getBaselineMark();
    const startMark = getStartMarkForMeasure(
      startMarkName !== undefined
        ? (performance.getEntriesByName(startMarkName)[0] as PerformanceMark)
        : undefined,
      baselineMark,
    );

    if (typeof startMarkName !== 'undefined' && !startMark) {
      return;
    }

    const measurement = measure(name, startMark?.name, endMarkName) as PerformanceMeasure;
    const adjustedMeasurement = {
      name,
      startTime:
        baselineMark !== undefined
          ? measurement.startTime - baselineMark.startTime
          : measurement.startTime,
      duration: measurement.duration,
    };

    logMeasurementToDD(adjustedMeasurement as PerformanceMeasure, {
      ...extra,
      routeChange: baselineMark !== undefined,
    });
    // eslint-disable-next-line no-empty
  } catch (e) {}
};
